import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, MessageCircle, Eye, ThumbsUp, Pin, Lock, Calendar, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CommunityTopicDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reply, setReply] = useState('');
  
  // Mock topic data - in production, fetch from API
  const topic = {
    id: parseInt(id),
    title: 'Best practices for complex implant referrals',
    author: 'Dr. William Gray',
    avatar: 'WG',
    category: 'best-practices',
    views: 342,
    isPinned: true,
    isLocked: false,
    createdAt: '2025-11-08',
    content: `I wanted to share some best practices I've developed over the years for managing complex implant referrals. These techniques have significantly improved our success rates and patient satisfaction.

**Key Points:**

1. **Comprehensive Initial Assessment**
   - Always request complete CBCT imaging before the referral
   - Document any systemic conditions that might affect healing
   - Include patient expectations and concerns

2. **Communication Protocol**
   - Establish a direct line with the referring dentist
   - Provide detailed treatment plans with alternatives
   - Follow up within 24 hours of consultation

3. **Patient Education**
   - Use 3D models to explain procedures
   - Provide written materials in plain language
   - Address financial concerns upfront

4. **Post-Op Coordination**
   - Schedule follow-up with referring dentist
   - Share detailed surgical notes
   - Maintain open communication throughout healing

What strategies have worked well in your practice? I'd love to hear your experiences!`
  };

  const [replies, setReplies] = useState([
    {
      id: 1,
      author: 'Dr. Sarah Johnson',
      avatar: 'SJ',
      content: 'Excellent points! I especially agree with the importance of CBCT imaging. We\'ve seen much better outcomes since making this a standard requirement.',
      timestamp: '2 hours ago',
      likes: 12
    },
    {
      id: 2,
      author: 'Dr. Michael Chen',
      avatar: 'MC',
      content: 'The communication protocol is crucial. We use a secure messaging platform that allows real-time updates. Has anyone tried something similar?',
      timestamp: '3 hours ago',
      likes: 8
    },
    {
      id: 3,
      author: 'Dr. Gerard Nikodem',
      avatar: 'GN',
      content: 'Great post! I would add that virtual consultations have been very helpful for complex cases. Allows for better planning before the patient even comes in.',
      timestamp: '5 hours ago',
      likes: 15
    },
    {
      id: 4,
      author: 'Dr. Elizabeth Fassler',
      avatar: 'EF',
      content: 'The 3D models for patient education are game-changers. We invested in a 3D printer and it\'s paid for itself in improved case acceptance.',
      timestamp: '1 day ago',
      likes: 10
    }
  ]);

  const handleReply = () => {
    if (!reply.trim()) return;
    
    const newReply = {
      id: replies.length + 1,
      author: user?.name || 'Anonymous User',
      avatar: user?.name?.split(' ').map(n => n[0]).join('') || 'AU',
      content: reply,
      timestamp: 'Just now',
      likes: 0
    };
    
    setReplies([...replies, newReply]);
    setReply('');
  };

  return (
    <div className="max-w-5xl mx-auto">
      <Button
        variant="ghost"
        onClick={() => navigate('/community')}
        className="mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Community
      </Button>

      {/* Topic Header */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center space-x-2 mb-4">
            <Badge variant="outline">{topic.category}</Badge>
            {topic.isPinned && (
              <Badge className="bg-[#D4AF37] text-[#0A1628]">
                <Pin className="h-3 w-3 mr-1" />
                Pinned
              </Badge>
            )}
            {topic.isLocked && (
              <Badge className="bg-gray-200 text-gray-700">
                <Lock className="h-3 w-3 mr-1" />
                Locked
              </Badge>
            )}
          </div>
          
          <CardTitle className="text-3xl mb-4">{topic.title}</CardTitle>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-[#0A1628] text-[#D4AF37] font-bold">
                  {topic.avatar}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-[#0A1628]">{topic.author}</p>
                <div className="flex items-center text-sm text-gray-600 space-x-3">
                  <span className="flex items-center">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(topic.createdAt).toLocaleDateString()}
                  </span>
                  <span className="flex items-center">
                    <Eye className="h-3 w-3 mr-1" />
                    {topic.views} views
                  </span>
                  <span className="flex items-center">
                    <MessageCircle className="h-3 w-3 mr-1" />
                    {replies.length} replies
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="prose max-w-none text-gray-800 whitespace-pre-wrap leading-relaxed">
            {topic.content}
          </div>
        </CardContent>
      </Card>

      {/* Replies Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageCircle className="h-5 w-5 mr-2 text-[#D4AF37]" />
            Replies ({replies.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Reply List */}
          {replies.map((rep, index) => (
            <div key={rep.id} className={`flex space-x-4 ${index !== 0 ? 'pt-6 border-t' : ''}`}>
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-[#0A1628] text-[#D4AF37] font-bold">
                  {rep.avatar}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-[#0A1628]">{rep.author}</p>
                    <p className="text-xs text-gray-500">{rep.timestamp}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-gray-600 hover:text-[#D4AF37]">
                    <ThumbsUp className="h-4 w-4 mr-1" />
                    {rep.likes}
                  </Button>
                </div>
                <p className="text-gray-700 leading-relaxed">{rep.content}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Add Reply */}
      {!topic.isLocked && (
        <Card>
          <CardHeader>
            <CardTitle>Add Your Reply</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Share your thoughts and experiences..."
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={5}
              className="mb-4"
            />
            <Button 
              onClick={handleReply}
              className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
              disabled={!reply.trim()}
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Post Reply
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CommunityTopicDetail;
